"use client";

import React from "react";
import Avatar, { AvatarProps } from "./Avatar";
import { getCoachInitials, stripGenderSuffix } from "@/utils/formatters/nameUtils";

export interface CoachAvatarProps
  extends Omit<AvatarProps, "alt" | "role" | "userType" | "fallbackText"> {
  /**
   * The coach's formatted display name, gender suffix and all
   * (e.g. "Jane D. (Female)"). Initials and alt text are derived from it.
   */
  name: string | null | undefined;
  /** Override the derived alt text when the surrounding copy already names the coach. */
  alt?: string;
}

/**
 * Coach avatar with a correct initials fallback.
 *
 * Every coach avatar in the app should go through this instead of Avatar
 * directly: display names carry a trailing gender label, and Avatar's generic
 * initials logic would turn "Jane D. (Female)" into "J(" or "JD" depending on
 * the caller. Centralizing it keeps the coach blue fallback, the storage-URL
 * proxying, and the broken-image fallback identical everywhere.
 */
const CoachAvatar: React.FC<CoachAvatarProps> = ({ name, alt, ...props }) => {
  const displayName = stripGenderSuffix(name) || "Coach";

  return (
    <Avatar
      alt={alt ?? displayName}
      fallbackText={getCoachInitials(name)}
      role="coach"
      {...props}
    />
  );
};

export default CoachAvatar;
